import { useRef, useState } from "react";
import type { MouseEvent } from "react";
import type { Menu } from "primereact/menu";

import { useEscFechaSoMenu } from "@/ds";
import { obraEmFechamento } from "@/modules/financeiro-obras";
import type {
  CentroCustoOpcao,
  EscopoMacroPlanejamento,
  MetaPlanejamento,
  PlanejamentoControle,
} from "@/modules/financeiro-obras";
import { useMediaQuery } from "@/hooks/use-media-query";
import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import { MOBILE_QUERY } from "../../../../financeiro-obras.constants";
import {
  PLANEJAMENTO_FORBIDDEN_MESSAGE,
  useOpcoesNovoTipoObraDados,
  usePlanejamentoControleDados,
} from "../../../hooks/use-planejamento-dados";
import { FEL_ZERO } from "./planejamento-controle.cancelamento";
import { montarCards } from "./planejamento-controle.cards";
import { montarCentrosParaEscopo } from "./planejamento-controle.escopo";
import {
  modoDaProjecao,
  montarObraDaProjecao,
  montarProjecao,
  motivoEdicaoBloqueada,
} from "./planejamento-controle.projecao";
import {
  cancelamentoBloqueado,
  cancelamentoTipoBloqueado,
  localizarEscopo,
  localizarTipo,
  montarEscopoACancelar,
  montarTipoACancelar,
} from "./planejamento-controle.cancelamento";
import {
  ADICIONAR_NOVO_ESCOPO,
  ADICIONAR_NOVO_TIPO,
  COLUNAS_CONFIGURACAO,
  COLUNAS_TRAVADAS_TOOLTIP,
  ICONES_ACAO,
  MENU_ESCOPO_CANCELAR,
  MENU_ESCOPO_EDITAR,
  MENU_ESCOPO_TRANSFERIR,
  MENU_TIPO_CANCELAR,
  MENU_TIPO_TRANSFERIR,
  MOTIVO_TRANSFERENCIA_PENDENTE,
  MOTIVO_CANCELAMENTO_PENDENTE,
  MOTIVO_REVISAO_PENDENTE,
  MOTIVO_CANCELAMENTO_TIPO_PENDENTE,
  MOTIVO_SEM_PERMISSAO_TRANSFERIR_TIPO,
  MOTIVO_TIPO_SEM_ESCOPO,
  MOTIVO_TRANSFERENCIA_TIPO_PENDENTE,
  MOTIVO_SEM_PERMISSAO_CANCELAR,
  MOTIVO_SEM_PERMISSAO_CANCELAR_TIPO,
  MOTIVO_SEM_PERMISSAO_TRANSFERIR,
  MOTIVO_NOVO_TIPO_EM_APROVACAO,
  MOTIVO_ESCOPO_SALDO_A_DESTINAR,
  MOTIVO_RESOLVER_PENDENCIA,
  MENU_ESCOPO_RESOLVER,
  MOTIVO_NOVO_TIPO_INATIVO,
  MOTIVO_SEM_NOVO_TIPO,
  MOTIVO_ESCOPO_EM_FECHAMENTO,
  MOTIVO_SEM_PERMISSAO_ESCOPO,
  MOTIVO_SEM_PERMISSAO_TIPO,
  ROTULOS_COLUNAS_CONFIGURACAO,
  VAZIO_DESCRIPTION,
  VAZIO_PENDENCIA_DESCRIPTION,
  VAZIO_PENDENCIA_TITLE,
  VAZIO_TITLE,
} from "./planejamento-controle.constants";
import { formatCompetencia } from "./planejamento-controle.format";
import {
  montarEscopoATransferir,
  montarTipoATransferir,
  tipoSemEscopoVigente,
  transferenciaBloqueada,
  transferenciaTipoBloqueada,
} from "./planejamento-controle.transferencia";
import type { TipoATransferir } from "./transferir-tipo-obra-dialog";
import { linhasVisiveis } from "./planejamento-controle.linhas";
import { useConfiguracaoTabelaPlanejamento } from "./use-configuracao-tabela-planejamento";
import { useExportarPlanejamento } from "./use-exportar-planejamento";
import type {
  EstadoPlanejamento,
  LinhaPlanejamento,
  MenuAcao,
  PlanejamentoControleProps,
} from "./types-planejamento-controle";

// Figma `plan-controle/tabela-arvore`: Nome (330; 220 no mobile, pedido do usuário: o 164 do Figma truncava o Tipo),
// Classificação 120, Total 108 e NC 108; um mês por competência (97); a sobra antes das ações (98),
// que ficam presas à direita como o `bloco-acoes`.
const COLUNAS_FIXAS = "330px 120px 108px 108px";
const COLUNAS_FIXAS_MOBILE = "220px 120px 108px 108px";
const COLUNA_MES = "97px";
const COLUNAS_FINAIS = "1fr 98px";
const COLUNAS_FINAIS_MOBILE = "1fr 76px";

/** Moeda do primeiro valor sem cotação em qualquer nó da composição (Obra, centro, Tipo ou Escopo). */
function moedaSemCotacaoDe({ totais, centrosCusto }: PlanejamentoControle): string | undefined {
  const nos = centrosCusto.flatMap((centro) => [
    centro.totais,
    ...centro.tiposObra.flatMap((tipo) => [
      tipo.totais,
      ...tipo.escoposMacro.map((escopo) => escopo.totais),
    ]),
  ]);

  return [totais, ...nos].find((no) => no && no.semCotacao.length > 0)?.semCotacao[0]?.moeda;
}

function motivoDaPendencia(
  escopo: EscopoMacroPlanejamento | false | null | undefined,
  padrao: string,
) {
  if (escopo && escopo.edicaoProjecao.bloqueio === "SALDO_A_DESTINAR")
    return MOTIVO_ESCOPO_SALDO_A_DESTINAR;
  return escopo && escopo.solicitacaoPendente === "REVISAO_PROJECAO"
    ? MOTIVO_REVISAO_PENDENTE
    : padrao;
}

function plural(quantidade: number, singular: string, pluralForma: string): string {
  return `${quantidade} ${quantidade === 1 ? singular : pluralForma}`;
}

/** Pendências estruturais do `meta` (HU 2709): o que ficou fora da árvore por falta de Centro de Custo ou de Tipo de Obra. */
function montarPendencias({ tiposSemCentroCusto, escoposSemTipo }: MetaPlanejamento): string[] {
  return [
    tiposSemCentroCusto > 0
      ? `${plural(tiposSemCentroCusto, "Tipo de Obra", "Tipos de Obra")} sem Centro de Custo`
      : null,
    escoposSemTipo > 0
      ? `${plural(escoposSemTipo, "Escopo Macro", "Escopos Macro")} sem Tipo de Obra`
      : null,
  ].filter((texto) => texto !== null);
}

function acao(
  key: string,
  label: string,
  onSelect: (() => void) | undefined,
  motivoSemAcao: string | null,
): MenuAcao {
  return {
    key,
    icone: ICONES_ACAO[key] ?? null,
    label,
    disabled: !onSelect,
    motivo: onSelect ? null : motivoSemAcao,
    onSelect: onSelect ?? (() => undefined),
  };
}

function motivoNovoTipoFechado(centros: CentroCustoOpcao[]): string | null {
  const tipos = centros.flatMap((centro) => centro.tiposObraElegiveis);
  if (
    centros.some(
      (centro) => centro.elegivel && centro.tiposObraElegiveis.some((t) => t.bloqueio === null),
    )
  )
    return null;
  if (tipos.some((tipo) => tipo.bloqueio === "INATIVO")) return MOTIVO_NOVO_TIPO_INATIVO;
  if (tipos.some((tipo) => tipo.bloqueio === "EM_APROVACAO")) return MOTIVO_NOVO_TIPO_EM_APROVACAO;
  return MOTIVO_SEM_NOVO_TIPO;
}

/** Aba Planejamento e Controle: estado da consulta, cards, cotação e a hierarquia expansível só no cliente. */
export function usePlanejamentoControle({
  obraId,
  onNovoTipoObra,
  onTransferirEscopo,
  onCancelarEscopo,
  onCancelarTipoObra,
}: PlanejamentoControleProps) {
  const { status: permissoesStatus, hasPermissao } = usePermissoes();
  // Sabendo que falta a claim, nem consulta: mostra "Acesso não autorizado".
  const semPermissao =
    permissoesStatus === "success" && !hasPermissao(HUB_PERMISSOES.FINANCEIRO_VIEW_DETALHE);
  const consulta = usePlanejamentoControleDados(obraId, !semPermissao);
  const planejamento = consulta.data;
  const podeAdicionarTipo =
    permissoesStatus === "success" && hasPermissao(HUB_PERMISSOES.FINANCEIRO_ADD_TIPO);
  const opcoesNovoTipo = useOpcoesNovoTipoObraDados(obraId, podeAdicionarTipo && !semPermissao);
  const exportacao = useExportarPlanejamento(obraId);
  const configuracaoTabela = useConfiguracaoTabelaPlanejamento();

  const [alternados, setAlternados] = useState<ReadonlySet<string>>(new Set());
  const [escopoAberto, setEscopoAberto] = useState<string | null>(null);
  const [novoTipoAberto, setNovoTipoAberto] = useState(false);
  const [novoEscopoAberto, setNovoEscopoAberto] = useState(false);
  const [escopoACancelar, setEscopoACancelar] = useState<string | null>(null);
  const [tipoACancelar, setTipoACancelar] = useState<string | null>(null);
  const [tipoATransferir, setTipoATransferir] = useState<TipoATransferir | null>(null);
  const [escopoATransferir, setEscopoATransferir] = useState<string | null>(null);
  const [escopoAProjetar, setEscopoAProjetar] = useState<string | null>(null);
  const [menuItens, setMenuItens] = useState<MenuAcao[]>([]);
  const [adicionarAberto, setAdicionarAberto] = useState(false);
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const menuRef = useRef<Menu>(null);
  const escMenu = useEscFechaSoMenu();

  // O item "Novo Tipo de Obra" só funciona com a claim de adicionar Tipo.
  const abrirNovoTipo =
    onNovoTipoObra ??
    (hasPermissao(HUB_PERMISSOES.FINANCEIRO_ADD_TIPO) ? () => setNovoTipoAberto(true) : undefined);

  const centrosDasOpcoes = opcoesNovoTipo.data?.centrosCusto ?? [];
  const motivoSemTipoIncluivel =
    opcoesNovoTipo.status === "success" ? motivoNovoTipoFechado(centrosDasOpcoes) : null;
  const carregandoOpcoes = podeAdicionarTipo && opcoesNovoTipo.status === "loading";
  let motivoNovoTipo: string | null =
    permissoesStatus === "success" ? MOTIVO_SEM_PERMISSAO_TIPO : null;
  if (podeAdicionarTipo) motivoNovoTipo = motivoSemTipoIncluivel;
  const abrirNovoTipoItem =
    motivoSemTipoIncluivel !== null || carregandoOpcoes ? undefined : abrirNovoTipo;

  const podeAdicionarEscopo = hasPermissao(HUB_PERMISSOES.FINANCEIRO_ADD_ESCOPO);
  const escopoBloqueadoPelaFase = obraEmFechamento(planejamento?.obra.fel);
  const abrirNovoEscopo =
    podeAdicionarEscopo && !escopoBloqueadoPelaFase ? () => setNovoEscopoAberto(true) : undefined;
  let motivoNovoEscopo: string | null =
    permissoesStatus === "success" ? MOTIVO_SEM_PERMISSAO_ESCOPO : null;
  if (podeAdicionarEscopo && escopoBloqueadoPelaFase)
    motivoNovoEscopo = MOTIVO_ESCOPO_EM_FECHAMENTO;

  const podeEditarDetalhe = hasPermissao(HUB_PERMISSOES.FINANCEIRO_EDIT_DETALHE);
  const podeProjetar = hasPermissao(HUB_PERMISSOES.FINANCEIRO_PROJECT_CUSTOS);
  const projecaoAberta =
    planejamento && escopoAProjetar ? montarProjecao(planejamento, escopoAProjetar) : null;
  const cancelarEscopoAberto =
    planejamento && escopoACancelar ? montarEscopoACancelar(planejamento, escopoACancelar) : null;

  const transferirEscopoAberto =
    planejamento && escopoATransferir
      ? montarEscopoATransferir(planejamento, escopoATransferir)
      : null;

  const cancelarTipoAberto =
    planejamento && tipoACancelar ? montarTipoACancelar(planejamento, tipoACancelar) : null;
  // Enquanto as permissões carregam, o item fica desabilitado sem o motivo de falta de permissão.
  const permissoesCarregadas = permissoesStatus === "success";
  const motivoSeCarregado = (bloqueado: boolean, motivo: string) =>
    bloqueado && (permissoesCarregadas || podeEditarDetalhe) ? motivo : null;

  const motivoEditar = (obraEscopoMacroId: string) =>
    planejamento && permissoesCarregadas
      ? motivoEdicaoBloqueada(planejamento, obraEscopoMacroId, podeProjetar)
      : null;

  function editarEscopo(obraEscopoMacroId: string) {
    setEscopoAberto(null);
    setEscopoAProjetar(obraEscopoMacroId);
  }

  function acaoEditarEscopo(obraEscopoMacroId: string): MenuAcao {
    const motivo = motivoEditar(obraEscopoMacroId);
    const liberado = permissoesCarregadas && planejamento !== undefined && motivo === null;

    return acao(
      "editar",
      MENU_ESCOPO_EDITAR,
      liberado ? () => editarEscopo(obraEscopoMacroId) : undefined,
      motivo,
    );
  }

  /** Item "Cancelar Escopo": abre o modal; sem a claim ou com solicitação pendente fica desabilitado com o motivo. */
  function acaoCancelarEscopo(obraEscopoMacroId: string): MenuAcao {
    const escopo = planejamento && localizarEscopo(planejamento, obraEscopoMacroId)?.escopo;
    const abrir = onCancelarEscopo
      ? () => onCancelarEscopo(obraEscopoMacroId)
      : () => setEscopoACancelar(obraEscopoMacroId);
    const bloqueado = !podeEditarDetalhe || (escopo ? cancelamentoBloqueado(escopo) : false);
    const motivo = podeEditarDetalhe
      ? motivoDaPendencia(escopo, MOTIVO_CANCELAMENTO_PENDENTE)
      : MOTIVO_SEM_PERMISSAO_CANCELAR;

    return acao(
      "cancelar",
      MENU_ESCOPO_CANCELAR,
      bloqueado ? undefined : abrir,
      motivoSeCarregado(bloqueado, motivo),
    );
  }

  /** Item "Transferir Escopo" (HU 2713): abre o fluxo; sem a claim ou com solicitação pendente fica desabilitado com o motivo. */
  function acaoTransferirEscopo(obraEscopoMacroId: string): MenuAcao {
    const escopo = planejamento && localizarEscopo(planejamento, obraEscopoMacroId)?.escopo;
    const abrir = onTransferirEscopo
      ? () => onTransferirEscopo(obraEscopoMacroId)
      : () => setEscopoATransferir(obraEscopoMacroId);
    const bloqueado = !podeEditarDetalhe || (escopo ? transferenciaBloqueada(escopo) : false);
    const motivo = podeEditarDetalhe
      ? motivoDaPendencia(escopo, MOTIVO_TRANSFERENCIA_PENDENTE)
      : MOTIVO_SEM_PERMISSAO_TRANSFERIR;

    return acao(
      "transferir",
      MENU_ESCOPO_TRANSFERIR,
      bloqueado ? undefined : abrir,
      motivoSeCarregado(bloqueado, motivo),
    );
  }

  /** Item "Cancelar Tipo de Obra" (HU 2717): mesma regra do Escopo, com a solicitação pendente do Tipo ou de qualquer Escopo dele. */
  function acaoCancelarTipo(obraTipoObraId: string): MenuAcao {
    const tipo = planejamento && localizarTipo(planejamento, obraTipoObraId)?.tipo;
    const abrir = onCancelarTipoObra
      ? () => onCancelarTipoObra(obraTipoObraId)
      : () => setTipoACancelar(obraTipoObraId);
    const bloqueado = !podeEditarDetalhe || (tipo ? cancelamentoTipoBloqueado(tipo) : false);
    const motivo = podeEditarDetalhe
      ? MOTIVO_CANCELAMENTO_TIPO_PENDENTE
      : MOTIVO_SEM_PERMISSAO_CANCELAR_TIPO;

    return acao(
      "cancelar",
      MENU_TIPO_CANCELAR,
      bloqueado ? undefined : abrir,
      motivoSeCarregado(bloqueado, motivo),
    );
  }

  function acaoTransferirTipo(obraTipoObraId: string): MenuAcao {
    const tipo = planejamento && localizarTipo(planejamento, obraTipoObraId)?.tipo;
    const bloqueado = !podeEditarDetalhe || (tipo ? transferenciaTipoBloqueada(tipo) : false);
    let motivo = MOTIVO_SEM_PERMISSAO_TRANSFERIR_TIPO;
    if (podeEditarDetalhe)
      motivo =
        tipo && tipoSemEscopoVigente(tipo)
          ? MOTIVO_TIPO_SEM_ESCOPO
          : MOTIVO_TRANSFERENCIA_TIPO_PENDENTE;

    return acao(
      "transferir",
      MENU_TIPO_TRANSFERIR,
      bloqueado || !planejamento
        ? undefined
        : () => setTipoATransferir(montarTipoATransferir(planejamento, obraTipoObraId)),
      motivoSeCarregado(bloqueado, motivo),
    );
  }

  const linhas = planejamento
    ? linhasVisiveis(planejamento.linhas, planejamento.competencias, alternados)
    : [];

  let estado: EstadoPlanejamento = "pronto";
  if (semPermissao || consulta.status === "forbidden") estado = "proibido";
  else if (consulta.status === "error") estado = "erro";
  else if (consulta.status === "loading" || !planejamento) estado = "carregando";
  else if (planejamento.centrosCusto.length === 0) estado = "vazio";

  function abrirMenu(evento: MouseEvent<HTMLElement>, itens: MenuAcao[]) {
    setMenuItens(itens);
    menuRef.current?.toggle(evento);
  }

  const competenciasTotal = planejamento?.competencias.length ?? 0;
  // Tipos que existem mas ficaram fora da árvore por falta de Centro de Custo: o vazio não pode dizer "sem Tipos".
  const tiposOcultos = (planejamento?.meta.tiposSemCentroCusto ?? 0) > 0;

  const moedaSemCotacao = planejamento ? moedaSemCotacaoDe(planejamento) : undefined;
  const cotacaoDefinida = planejamento?.cotacao ?? null;
  const cotacaoPendente = cotacaoDefinida
    ? null
    : (planejamento?.cotacaoPendente ??
      (moedaSemCotacao ? { moeda: moedaSemCotacao, moedaId: null } : null));
  const cotacaoCard =
    cotacaoDefinida || cotacaoPendente
      ? {
          obraId,
          cotacao: cotacaoDefinida
            ? {
                moedaId: cotacaoDefinida.moedaId,
                moeda: cotacaoDefinida.moeda,
                valor: cotacaoDefinida.valor,
                solicitacao: cotacaoDefinida.solicitacao,
              }
            : null,
          pendente: cotacaoPendente,
          fel: planejamento?.obra.fel ?? FEL_ZERO,
          editavel: podeEditarDetalhe,
          podeDefinir: podeEditarDetalhe || podeProjetar,
          onAlterada: consulta.reload,
        }
      : null;

  return {
    obraId,
    estado,
    errorMessage: semPermissao ? PLANEJAMENTO_FORBIDDEN_MESSAGE : consulta.errorMessage,
    recarregar: consulta.reload,
    cards: planejamento ? montarCards(planejamento) : [],
    cotacaoCard,
    linhas,
    competencias: (planejamento?.competencias ?? []).map((competencia) =>
      formatCompetencia(competencia),
    ),
    // `repeat(0, …)` é CSS inválido e derruba o template inteiro: sem competência, só as colunas fixas e as ações.
    colunasTabela: [
      isMobile ? COLUNAS_FIXAS_MOBILE : COLUNAS_FIXAS,
      ...(competenciasTotal > 0 ? [`repeat(${competenciasTotal}, ${COLUNA_MES})`] : []),
      isMobile ? COLUNAS_FINAIS_MOBILE : COLUNAS_FINAIS,
    ].join(" "),
    tabelaCompacta: configuracaoTabela.compacta,
    abrirExportacao: exportacao.open,
    abrirConfiguracaoTabela: configuracaoTabela.dialog.open,
    exportacao: {
      aberta: exportacao.isOpen,
      exportando: exportacao.isExporting,
      exportar: exportacao.run,
      fechar: exportacao.close,
    },
    configuracaoTabela: {
      dialog: configuracaoTabela.dialog,
      columnLabels: ROTULOS_COLUNAS_CONFIGURACAO,
      mainColumns: COLUNAS_CONFIGURACAO,
      lockedColumnsTooltip: COLUNAS_TRAVADAS_TOOLTIP,
      onApplied: configuracaoTabela.notificarAplicada,
    },
    pendencias: planejamento ? montarPendencias(planejamento.meta) : [],
    vazio: tiposOcultos
      ? { titulo: VAZIO_PENDENCIA_TITLE, descricao: VAZIO_PENDENCIA_DESCRIPTION }
      : { titulo: VAZIO_TITLE, descricao: VAZIO_DESCRIPTION },
    menuRef,
    menuItens,
    // O ângulo do Adicionar vira para cima enquanto o menu dele está aberto (Figma `add` Variant2).
    adicionarAberto,
    mostrarMenu: escMenu.onShow,
    fecharMenu: () => {
      escMenu.onHide();
      setAdicionarAberto(false);
    },
    teclasMenu: { onKeyDownCapture: escMenu.onKeyDownCapture, onKeyDown: escMenu.onKeyDown },
    alternar: (key: string) =>
      setAlternados((atual) => {
        const proximo = new Set(atual);
        if (!proximo.delete(key)) proximo.add(key);
        return proximo;
      }),
    abrirDetalhe: (obraEscopoMacroId: string) => setEscopoAberto(obraEscopoMacroId),
    fecharDetalhe: () => setEscopoAberto(null),
    novoTipo:
      novoTipoAberto && planejamento
        ? { obraId, fel: planejamento.obra.fel, opcoes: opcoesNovoTipo }
        : null,
    fecharNovoTipo: () => setNovoTipoAberto(false),
    novoEscopo:
      novoEscopoAberto && planejamento
        ? {
            obraId,
            fel: planejamento.obra.fel,
            obraInicio: planejamento.obra.dataInicio,
            obraFim: planejamento.obra.dataFim,
            ...montarObraDaProjecao(planejamento),
            centrosCusto: montarCentrosParaEscopo(planejamento),
            opcoesNovoTipo: podeAdicionarTipo ? opcoesNovoTipo : null,
          }
        : null,
    fecharNovoEscopo: () => setNovoEscopoAberto(false),
    concluirNovoEscopo: () => {
      setNovoEscopoAberto(false);
      consulta.reload();
      if (podeAdicionarTipo) opcoesNovoTipo.reload();
    },
    // Recarrega a composição: o Tipo novo aparece (pendente, a partir do FEL-1).
    concluirNovoTipo: () => {
      setNovoTipoAberto(false);
      consulta.reload();
      opcoesNovoTipo.reload();
    },
    cancelarEscopo: cancelarEscopoAberto ? { obraId, escopo: cancelarEscopoAberto } : null,
    fecharCancelarEscopo: () => setEscopoACancelar(null),
    // Recarrega a composição: FEL-0 tira o Escopo da tabela; FEL-1+ mostra a tag de cancelamento pendente.
    concluirCancelarEscopo: () => {
      setEscopoACancelar(null);
      consulta.reload();
    },
    transferirEscopo: transferirEscopoAberto ? { obraId, escopo: transferirEscopoAberto } : null,
    fecharTransferirEscopo: () => setEscopoATransferir(null),
    // Recarrega a composição: o Escopo continua na Obra, agora com a tag de transferência aguardando aprovação.
    concluirTransferirEscopo: () => {
      setEscopoATransferir(null);
      consulta.reload();
    },
    transferirTipo: tipoATransferir ? { obraId, tipo: tipoATransferir } : null,
    recarregarComposicao: () => consulta.reload(),
    fecharTransferirTipo: () => setTipoATransferir(null),
    concluirTransferirTipo: () => {
      setTipoATransferir(null);
      consulta.reload();
    },
    cancelarTipo: cancelarTipoAberto ? { obraId, tipo: cancelarTipoAberto } : null,
    fecharCancelarTipo: () => setTipoACancelar(null),
    // Recarrega a composição: FEL-0 tira o Tipo da tabela e o sucessor vira dominante; FEL-1+ mostra a tag de cancelamento pendente.
    concluirCancelarTipo: () => {
      setTipoACancelar(null);
      consulta.reload();
    },
    detalhe: escopoAberto
      ? {
          obraId,
          obraEscopoMacroId: escopoAberto,
          editar: {
            motivo: motivoEditar(escopoAberto),
            abrir: () => editarEscopo(escopoAberto),
            liberado:
              permissoesCarregadas &&
              planejamento !== undefined &&
              motivoEditar(escopoAberto) === null,
          },
        }
      : null,
    projecao:
      projecaoAberta && planejamento && escopoAProjetar
        ? { obraId, modo: modoDaProjecao(planejamento, escopoAProjetar), ...projecaoAberta }
        : null,
    fecharProjecao: () => setEscopoAProjetar(null),
    concluirProjecao: () => {
      setEscopoAProjetar(null);
      consulta.reload();
    },
    cotacaoAlterada: consulta.reload,
    abrirMenuLinha: (evento: MouseEvent<HTMLElement>, linha: LinhaPlanejamento) =>
      abrirMenu(
        evento,
        linha.menu === "escopo"
          ? [
              ...(linha.saldoADestinar
                ? [acao("resolver", MENU_ESCOPO_RESOLVER, undefined, MOTIVO_RESOLVER_PENDENCIA)]
                : []),
              acaoEditarEscopo(linha.entidadeId),
              acaoTransferirEscopo(linha.entidadeId),
              acaoCancelarEscopo(linha.entidadeId),
            ]
          : [acaoTransferirTipo(linha.entidadeId), acaoCancelarTipo(linha.entidadeId)],
      ),
    abrirMenuAdicionar: (evento: MouseEvent<HTMLElement>) => {
      setAdicionarAberto(true);
      abrirMenu(evento, [
        acao("novo-tipo", ADICIONAR_NOVO_TIPO, abrirNovoTipoItem, motivoNovoTipo),
        acao("novo-escopo", ADICIONAR_NOVO_ESCOPO, abrirNovoEscopo, motivoNovoEscopo),
      ]);
    },
  };
}
